import type { ExerciseDto, EquipmentDto } from '../../../api/health';

export const MUSCLE_GROUPS = [
  { slug: 'chest', label: 'Chest' },
  { slug: 'back', label: 'Back' },
  { slug: 'biceps', label: 'Biceps' },
  { slug: 'triceps', label: 'Triceps' },
  { slug: 'quadriceps', label: 'Quadriceps' },
  { slug: 'hamstrings', label: 'Hamstrings' },
  { slug: 'shoulders', label: 'Shoulders' },
  { slug: 'hips', label: 'Hips' },
  { slug: 'waist', label: 'Waist' },
  { slug: 'upper-arms', label: 'Upper Arms' },
  { slug: 'calves', label: 'Calves' },
  { slug: 'forearms', label: 'Forearms' },
  { slug: 'neck', label: 'Neck' },
] as const;

export const muscleLabel = (slug: string | null): string =>
  MUSCLE_GROUPS.find((m) => m.slug === slug)?.label ?? slug ?? '';

export const muscleIconSrc = (slug: string | null): string | null =>
  slug && MUSCLE_GROUPS.some((m) => m.slug === slug) ? `/health/muscles/${slug}.svg` : null;

/** Display-group the full type enum down to the design's labels. */
export const typeLabel = (type: ExerciseDto['type']): string => {
  if (type === 'cardio_steady' || type === 'cardio_intervals') return 'Cardio';
  if (type === 'hold') return 'Hold';
  if (type === 'mobility') return 'Mobility';
  if (type === 'plyometric') return 'Plyometric';
  return 'Strength';
};

export const levelLabel = (level: ExerciseDto['level']): string =>
  level ? level.charAt(0).toUpperCase() + level.slice(1) : '—';

export const equipmentName = (id: string | null, all: EquipmentDto[] | undefined): string =>
  (id && all?.find((e) => e.id === id)?.name) || '—';

/** Cover/front/back figure. Prefers a stored image URL, else the primary-muscle SVG. */
export const ExerciseFigure = ({
  ex,
  view = 'cover',
  size = 150,
}: {
  ex: Pick<ExerciseDto, 'primaryMuscle' | 'coverImageUrl' | 'frontImageUrl' | 'backImageUrl'>;
  view?: 'cover' | 'front' | 'back';
  size?: number;
}) => {
  const stored =
    view === 'front' ? ex.frontImageUrl : view === 'back' ? ex.backImageUrl : ex.coverImageUrl;
  const url = stored || muscleIconSrc(ex.primaryMuscle);
  if (url) {
    return (
      <img
        src={url}
        width={size}
        height={size}
        alt=""
        style={{ display: 'block', opacity: view === 'back' && !ex.backImageUrl ? 0.7 : 1 }}
      />
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 40 80" style={{ display: 'block' }} aria-hidden>
      <g fill="var(--ink-5)">
        <circle cx="20" cy="11" r="6" />
        <rect x="13" y="18" width="14" height="24" rx="5" />
      </g>
    </svg>
  );
};

export const EquipGlyph = ({
  iconKind,
  selected = false,
}: {
  iconKind: string;
  selected?: boolean;
}) => {
  const glyph = iconKind.startsWith('glyph:') ? iconKind.slice(6) : null;
  const kind = glyph ? '' : iconKind;

  const stroke = selected ? '#fff' : '#3a3a3a';
  const fill = selected ? '#fff' : '#2a2a2a';

  if (glyph) {
    return (
      <span style={{ fontSize: 22, fontWeight: 600, color: selected ? '#fff' : 'var(--ink-3)' }}>
        {glyph}
      </span>
    );
  }

  const S: React.SVGProps<SVGSVGElement> = {
    width: 40,
    height: 40,
    viewBox: '0 0 40 40',
    fill: 'none',
    stroke,
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };

  switch (kind) {
    case 'barbell':
      return (
        <svg {...S}>
          <path d="M5 20h30M8 14v12M12 16v8M28 16v8M32 14v12" />
        </svg>
      );
    case 'dumbbell':
      return (
        <svg {...S}>
          <path d="M14 20h12M9 14v12M13 16v8M27 16v8M31 14v12" />
        </svg>
      );
    case 'kettlebell':
      return (
        <svg {...S} fill={fill} stroke="none">
          <path d="M20 12a4 4 0 00-3 6.5C14 20 13 24 13 28h14c0-4-1-8-4-9.5A4 4 0 0020 12z" />
        </svg>
      );
    case 'ball':
      return (
        <svg {...S} fill={fill} stroke="none">
          <circle cx="20" cy="20" r="11" />
          <circle cx="20" cy="20" r="4" fill={selected ? '#3a6aa8' : '#fff'} />
        </svg>
      );
    case 'plate':
      return (
        <svg {...S} fill={fill} stroke="none">
          <circle cx="20" cy="20" r="11" />
          <circle cx="20" cy="20" r="3" fill={selected ? '#3a6aa8' : '#fff'} />
        </svg>
      );
    case 'band':
      return (
        <svg {...S}>
          <path d="M14 12v9a6 6 0 0012 0v-9M14 12h-2M26 12h2" />
        </svg>
      );
    case 'rope':
      return (
        <svg {...S}>
          <path d="M13 12c4 3 0 7 4 10s0 4 0 6M27 12c-4 3 0 7-4 10s0 4 0 6" />
        </svg>
      );
    case 'dome':
      return (
        <svg {...S}>
          <path d="M8 26a12 7 0 0124 0z" fill={fill} stroke="none" />
          <path d="M8 26h24" />
        </svg>
      );
    case 'roll':
      return (
        <svg {...S} fill={fill} stroke="none">
          <rect x="8" y="15" width="24" height="10" rx="5" />
        </svg>
      );
    case 'balls':
      return (
        <svg {...S} fill={fill} stroke="none">
          <circle cx="15" cy="16" r="4" />
          <circle cx="25" cy="17" r="4.5" />
          <circle cx="18" cy="25" r="3.5" />
        </svg>
      );
    case 'suspension':
      return (
        <svg {...S}>
          <path d="M20 10v6M20 16l-6 12M20 16l6 12M14 28h-2M26 28h2" />
        </svg>
      );
    case 'trap':
      return (
        <svg {...S}>
          <path d="M12 14h16v12H12zM8 18v4M32 18v4" />
        </svg>
      );
    case 'wheel':
      return (
        <svg {...S} fill={fill} stroke="none">
          <circle cx="20" cy="20" r="9" />
          <rect x="6" y="18.5" width="28" height="3" rx="1.5" />
          <circle cx="20" cy="20" r="3" fill={selected ? '#3a6aa8' : '#fff'} />
        </svg>
      );
    case 'machine':
    default:
      return (
        <svg {...S}>
          <path d="M10 12v16M30 12v16M10 16h20M10 24h20" />
        </svg>
      );
  }
};
